import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { logout, selectAccountId, selectAuthenticatedUserToken } from '../../store/auth/authSlice'
import { Token } from '../../common/token'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useUTMHandler } from '../../common/utm'
import { MFAModal } from './MFAModal'
import './MFALogin.scss'
import { ReactComponent as ZocksLogoFullBlack } from '../../assets/ZocksLogoFullBlack.svg'
import { useLazyGetConfigQuery, useLazyListFactorsQuery } from '../../store/mfa/mfa'
import { MFAFactor, MFAConfig } from '../../models/MFA'
import { resetFlow, setupMFAForLogin, toClaimCodePage } from '../../store/mfa/mfaSlice'
import { Button } from '../../components/Button/Button'

const LoginHeader: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  return (
    <div className='auth-header'>
      <Button
        onClick={() => {
          dispatch(logout(location))
          navigate('/mfa')
        }}
      >
        <ZocksLogoFullBlack className='auth-logo' />
      </Button>
    </div>
  )
}

const getDefaultFactor = (factors: MFAFactor[]): MFAFactor => {
  return factors.find((factor) => factor.default) ?? factors[0]
}

const calculateTokenExpirationTimeLeft = (tokenExpiration: number): number => {
  const now = new Date().getTime()
  return Math.max(tokenExpiration * 1000 - now, 0)
}

const selectMFAFactorForLogin = (factors: MFAFactor[], config?: MFAConfig | undefined): MFAFactor => {
  const requiredTypes = config?.factorType
  if (requiredTypes && requiredTypes.length > 0) {
    return factors.find((f) => requiredTypes.includes(f.type)) ?? getDefaultFactor(factors)
  }
  return getDefaultFactor(factors)
}

export const MFALogin: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUserToken = useAppSelector(selectAuthenticatedUserToken)
  const { appendUTMParams } = useUTMHandler()
  const [showMFAModal, setShowMFAModal] = useState(true)
  const [loading, setLoading] = useState<boolean>(true)
  const dispatch = useAppDispatch()

  const [listFactors] = useLazyListFactorsQuery()
  const [getConfig] = useLazyGetConfigQuery()

  useEffect(() => {
    if (authenticatedUserToken) {
      const token = Token.fromSerialized(authenticatedUserToken)
      const tokenExpirationTimeout = setTimeout(() => {
        dispatch(logout(location))
        navigate('/mfa')
      }, calculateTokenExpirationTimeLeft(token.accessTokenExpiration))
      return () => {
        clearTimeout(tokenExpirationTimeout)
      }
    }
  }, [authenticatedUserToken])

  useEffect(() => {
    const initializeFlow = async () => {
      try {
        const [{ data: factors }, { data: config }] = await Promise.all([listFactors({ accountId }), getConfig(accountId)])
        if (!factors || factors.length === 0) {
          dispatch(setupMFAForLogin())
          return
        }
        const selectedFactor = selectMFAFactorForLogin(factors, config)
        dispatch(toClaimCodePage(selectedFactor))
      } finally {
        setLoading(false)
      }
    }
    dispatch(resetFlow())
    setLoading(true)
    initializeFlow()
  }, [])

  if (authenticatedUserToken) {
    const token = Token.fromSerialized(authenticatedUserToken)
    if (token.tokenUse !== 'mfa') {
      return <Navigate to={appendUTMParams('/', true)} replace />
    }
  }

  const handleHideMFAModal = () => {
    setShowMFAModal(false)
  }

  return (
    <>
      <LoginHeader />
      <MFAModal show={showMFAModal} onHide={handleHideMFAModal} login={true} loading={loading} />
    </>
  )
}
